<template>
  <transition name="slide-fade">
    <VDetailBox v-if="!isEmpty" ref="history" :width="3" table :negative="isError" headline="order.history">
      <template #default>
        <transition name="fade" mode="out-in">
          <div v-if="isLoading" key="loading">
            <slot name="loading"><VSpinner class="offset-top-4"/></slot>
          </div>
          <div v-else-if="isError" key="error" class="offset-top-2 offset-bottom-2 offset-left-2">
            <div>{{ errorMessage }}</div>
            <VButton v-if="isErrorRetryable" class="btn-white btn-small offset-top-1" label="common.retry" @click="onLoad" />
          </div>
          <VueGoodTable v-else-if="hasContent" key="table" style-class="table table-hover" :columns="columns" :rows="content">
            <template #table-column="{ column }">
              <VTableHeader :columns="columns" :column="column" />
            </template>
          </VueGoodTable>
        </transition>
      </template>
    </VDetailBox>
    <VButton v-else class="btn-small btn-white offset-top-1 center" label="order.btnShowHistory" @click="onLoad" />
  </transition>
</template>

<script>
import Proxy from '@/proxies/SubstoreOrderOverviewProxy';
import VButton from '@/components/Button';
import VDetailBox from '@/components/UI/DetailBox';
import { formatDateTime } from '@/utils/date';

export default {
  name: 'OrderOverviewItemHistory',

  components: {
    VButton,
    VDetailBox,
    VueGoodTable: () => import('vue-good-table').then(m => m.VueGoodTable),
    VTableHeader: () => import('@/components/Table/Header'),
    VSpinner: () => import('@/components/Spinner'),
  },

  props: {
    subscriberIdentifierType: {
      type: String,
      required: true,
    },
    uniqueOrderId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      // valid contents: 'EMPTY', 'LOADING', 'ERROR', [rows]
      content: 'EMPTY',
      isErrorRetryable: false,
      errorCode: null,
      columns: [
        {
          label: 'order.validFrom_label',
          tooltip: 'order.validFrom_tooltip',
          field: 'validFrom',
          sortable: false,
          type: 'string',
          formatFn: this.translateCell,
        },
        {
          label: 'order.validTo_label',
          tooltip: 'order.validTo_tooltip',
          field: 'validTo',
          sortable: false,
          type: 'string',
          formatFn: this.translateCell,
        },
        {
          label: 'order.dueDate_label',
          tooltip: 'order.dueDate_tooltip',
          field: 'dueDate',
          sortable: false,
          type: 'string',
          formatFn: this.translateCell,
        },
        {
          label: 'order.status_label',
          tooltip: 'order.status_tooltip',
          field: 'orderStatus',
          sortable: false,
          type: 'string',
          formatFn: this.translateCell,
        },
        {
          label: 'order.reason_label',
          tooltip: 'order.reason_tooltip',
          field: 'stateChangeReason',
          sortable: false,
          type: 'string',
        },
        {
          label: 'order.information_label',
          tooltip: 'order.information_tooltip',
          field: 'stateChangeInfoText',
          sortable: false,
          type: 'string',
        },
      ],
    };
  },

  computed: {
    isEmpty() {
      return this.content === 'EMPTY';
    },

    isLoading() {
      return this.content === 'LOADING';
    },

    isError() {
      return this.content === 'ERROR';
    },

    hasContent() {
      return Array.isArray(this.content);
    },

    errorMessage() {
      return this.$t(this.errorCode === 'EMPTY_RESPONSE' ? 'errorcodes.ORDER_DETAILS_EMPTY_RESPONSE' : 'errorcodes.ORDER_DETAILS_ERROR');
    },
  },

  methods: {
    async onLoad() {
      this.setLoading();
      try {
        const response = await new Proxy().history(this.subscriberIdentifierType, this.uniqueOrderId);
        this.setContent(this.translateRows(response.data.data));
        this.$nextTick(() => {
          this.$refs.history.focus();
        });
      } catch (error) {
        this.setError(error.data?.args?.code, error.data?.codeType !== 'FATAL');
      }
    },

    translateRows(rows) {
      if (rows.length < 1) return rows;
      rows = rows.reverse().map(row => {
        row.orderStatus = `order.orderState_${row.orderStatus}`;
        return row;
      });
      for (let i = 0; i < rows.length - 1; ++i) {
        rows[i].validTo = rows[i + 1].validFrom;
      }
      rows[rows.length - 1].validTo = 'substore.unlimited';
      return rows;
    },

    translateCell(cell) {
      if (this.$te(cell)) {
        return this.$t(cell);
      } else
        try {
          return formatDateTime(cell);
        } catch {
          return cell;
        }
    },

    setLoading() {
      this.content = 'LOADING';
    },

    setError(code, retryable) {
      this.errorCode = code;
      this.isErrorRetryable = retryable;
      this.content = 'ERROR';
    },

    setContent(content) {
      this.content = content;
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@scss';

.center {
  margin-right: auto;
  margin-left: auto;
}
</style>
